import { SupplierUserRole } from './enums';

/**
 * Typed view of the integration array returned by the SupplierDetailsFragment.
 * Each element is one of the four option variants the GraphQL union can return.
 */
export type SupplierIntegrationEntry =
  | {
      __typename: 'IntegrationEmailOption';
      /** Deprecated upstream, round-tripped so a save does not delete it. */
      emailAddress?: string | null;
      orderEmails?: string[] | null;
      messageEmails?: string[] | null;
      complaintEmails?: string[] | null;
      pdf: boolean;
      xlsx: boolean;
    }
  | { __typename: 'IntegrationWhatsAppOption'; phone: string }
  | { __typename: 'IntegrationSmsOption'; phone: string }
  // Deprecated and not shown in the form. The phone is read anyway because
  // `supplierUpdate` replaces the whole array: without it a save would delete
  // the fax route.
  | { __typename: 'IntegrationFaxOption'; phone: string };

/**
 * Presigned-upload image record returned by the supplier detail query.
 * `uploadUrl` is a short-lived (~15 min) presigned S3 PUT URL — persistence is
 * automatic server-side once the PUT succeeds (S3 event → SQS handler); there
 * is no save mutation. The size crops are produced asynchronously by the image
 * service after upload.
 */
/**
 * The access package values the backend stores.
 *
 * Lower-camel, not the capitalised labels the UI shows, so a comparison against
 * `'Premium'` type-checks and never matches. Kept here rather than read from the
 * generated schema because components must not import from `generated`.
 */
export const ACCESS_PACKAGE = {
  FREE: 'free',
  LEAD_GEN: 'leadGen',
  PREMIUM: 'premium',
  TRIAL: 'trial',
  VERIFIED: 'verified',
} as const;

/** One of the stored access-package values. */
export type AccessPackageCode =
  (typeof ACCESS_PACKAGE)[keyof typeof ACCESS_PACKAGE];

/**
 * The eCommerce Advanced features, named as `Supplier.eCommAdvancedSettings`
 * names them. Each one is switched on or off on its own.
 */
export const ECOMM_ADVANCED_FEATURES = [
  'searchSynonyms',
  'productBoosting',
] as const;

/** One eCommerce Advanced feature. */
export type ECommAdvancedFeature = (typeof ECOMM_ADVANCED_FEATURES)[number];

/** Whether each eCommerce Advanced feature is on for a distributor. */
export type ECommAdvancedFeatureStates = Record<ECommAdvancedFeature, boolean>;

export interface SupplierCoverPhoto {
  url?: string | null;
  uploadUrl?: string | null;
  /** 375 x 200 crop */
  smallRectangle?: string | null;
  /** 750 x 400 crop */
  mediumRectangle?: string | null;
  /** 1125 x 600 crop */
  largeRectangle?: string | null;
}

export interface SupplierBotUserProfileImage {
  uploadUrl?: string | null;
  /** 300 x 300 crop */
  smallSquare?: string | null;
  /** 600 x 600 crop */
  mediumSquare?: string | null;
  /** 900 x 900 crop */
  largeSquare?: string | null;
}

/**
 * Supplier list item type for table display
 */
/**
 * A supplier's delivery area, as a polygon of geographic points.
 *
 * A supplier is only offered to buyers whose coordinates fall inside one of
 * these polygons, and a supplier with none is never treated as verified by the
 * backend, so it stays out of buyer-app search entirely.
 */
export interface DeliveryZonePoint {
  latitude: number;
  longitude: number;
}

export interface DeliveryZone {
  name: string;
  points: DeliveryZonePoint[];
}

/** A catalog product's stock status. */
export type StockStatus = 'InStock' | 'OutOfStock' | 'Discontinued';

export interface SupplierListItem {
  id: string;
  name?: string | null;
  description?: string | null;
  primaryAssortment?: string | null;
  secondaryAssortments?: string[] | null;
  deliveryCosts?: string | null;
  minOrder?: {
    price?: {
      currency?: string | null;
      amount?: number | null;
    } | null;
    strict?: boolean | null;
  } | null;
  botUserId?: string | null;
  botUser?: {
    profileImage?: SupplierBotUserProfileImage | null;
  } | null;
  coverPhoto?: SupplierCoverPhoto | null;
  contactEmails?: string[] | null;
  referenceId?: string | null;
  /**
   * The vendor's own code for this supplier, from their ERP or other systems.
   * Unique within the supplier's hierarchy root.
   */
  code?: string | null;
  /** The vendor's own name for this supplier, from their ERP or other systems. */
  externalName?: string | null;
  accessPackage?: string | null;
  /**
   * Which eCommerce Advanced features are on. Null when they could not be read,
   * which is not the same as every feature off.
   */
  eCommAdvancedFeatures?: ECommAdvancedFeatureStates | null;
  isIntegrated?: boolean | null;
  locale?: string | null;
  /**
   * IANA zone name, e.g. 'Europe/Berlin'. Drives the distributor's delivery
   * days, lead times and cutoff times. Absent means the backend falls back to
   * America/Los_Angeles.
   */
  timezone?: string | null;
  /**
   * Needed on save, not for display. `supplierUpdate` derives the bot user's
   * `isTest` and `chocoUser` from `!!supplier.isTest` on every update, so the
   * payload has to carry the current value or both flags get cleared.
   */
  isTest?: boolean | null;
  /**
   * Dogfooding flag from the backend.
   *
   * This portal's integration-settings gate treats puppy distributors as test
   * distributors too, so the loaded value needs to reach the form even though it
   * is not shown directly.
   */
  isPuppy?: boolean | null;
  // Order preferences
  allowCustomProducts?: boolean | null;
  customerNumberMandatory?: boolean | null;
  deliveryDateMandatory?: boolean | null;
  allowBuyerComment?: boolean | null;
  /**
   * Whether checkout calls the promotion engine for this distributor. Unset
   * means off: the backend treats both of these as opt-in (PRO-279).
   */
  enablePromotions?: boolean | null;
  /** Whether checkout calls the tax engine for this distributor. */
  enableTaxes?: boolean | null;
  /**
   * Which date drives price, promotion and tax evaluation at ordering time.
   * Unset means the backend uses ORDER_DATE.
   */
  pricingDateSource?: 'ORDER_DATE' | 'DELIVERY_DATE' | null;
  /**
   * Decimal places used when computing prices and taxes. Unset means the
   * backend uses the currency's own minor units, so two for most currencies.
   */
  calculationPrecision?: number | null;
  hasEnabledFractionalUnits?: boolean | null;
  allowOnlyOneActiveVariant?: boolean | null;
  /**
   * Whether buyers may build an order guide by photographing an invoice.
   */
  allowInvoiceOrderGuidePhotoUpload?: boolean | null;
  /** Whether the customer number on an order is locked once set. */
  disableCustomerNumberEdits?: boolean | null;
  /** Whether only supplier admins, not sales reps, may edit customer delivery days and cutoff times. */
  restrictSalesRepDeliverySettingEdits?: boolean | null;
  /**
   * Whether merging two lines of the same product is turned OFF.
   *
   * The name is inverted and the stored value is what it says here: `true` stops
   * lines merging. `smartOrderService` reads it through a gateway that negates it
   * (`supplier.gateway.ts`, CHAI-2903), which is where the "allow" reading comes
   * from. Anything reading this field directly, this portal included, gets the
   * "disabled" meaning. Turning it on also stops orders being edited.
   */
  allowOrderedProductMerging?: boolean | null;
  /** Whether the eCommerce catalog shows products as a grid. */
  enableEcommerceProductGridView?: boolean | null;
  /** Whether eCommerce checkout collects a PO number. */
  allowEcommercePoNumber?: boolean | null;
  /**
   * Whether buyers may enquire about new business with this distributor.
   *
   * Only editable on a Premium distributor. A LeadGen distributor has acquisition
   * regardless of this flag, so the flag alone does not answer "is acquisition
   * on".
   */
  allowCustomerAcquisition?: boolean | null;
  sortCartBy?: 'alphabetical' | 'orderGuide' | null;

  // Catalog settings
  noDefaultCatalog?: boolean | null;
  defaultSubCatalog?: string | null;
  priceSetting?: {
    displayInMarketplace: boolean;
    displayToCustomers: boolean;
  } | null;
  hideMyPrice?: boolean | null;
  /** Which stock statuses buyers see in the catalog. Null means in stock only. */
  buyerVisibleStockStatuses?: StockStatus[] | null;

  // Notification/integration settings
  integration?: SupplierIntegrationEntry[] | null;

  // Delivery areas used for buyer-app discoverability
  deliveryZones?: DeliveryZone[] | null;
}

/**
 * Team member type for supplier/distributor team management.
 * Mirrors BuyerTeamMember, but the role is the user's supplier role
 * (`supplierAdmins` / `supplierSalesReps`).
 */
export interface SupplierTeamMember {
  id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: SupplierUserRole | null;
  profileImage?: {
    url?: string | null;
  } | null;
}

/**
 * A request from a user to join a distributor's team, awaiting a decision.
 * Mirrors BuyerJoinRequest — the backend type (`UserRequestToJoin`) is shared,
 * only the entity it points at differs.
 */
export interface SupplierJoinRequest {
  id: string;
  user?: {
    id: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}
